import { forwardRef } from 'react';

import { IELECT_MARK_DATA_URL } from '../../../assets/images/ielect-mark-data';
import { partyInitials } from '../../../utils/partyInitials';
import { CertificateSheet } from './styled';
import { formatSubmittedDate, fullName, roleLabel, type SuccessPayload } from './types';

interface CertificateProps {
	payload: SuccessPayload;
}

// The off-screen A4-landscape credential rasterised into the downloadable PDF.
// Text + CSS + inline SVG only — no external images — so html2canvas stays
// untainted and within the app CSP (img-src 'self' data: blob:).
const Certificate = forwardRef<HTMLDivElement, CertificateProps>(({ payload }, ref) => {
	const name = fullName(payload) || 'Volunteer';
	const party = payload.partyLongName || payload.partyName || '';

	return (
		<CertificateSheet ref={ref}>
			<div className='cert-frame'>
				<div className='cert-head'>
					<div className='cert-brand'>
						<img
							className='mark'
							src={IELECT_MARK_DATA_URL}
							alt=''
						/>
						<div>
							<div className='word'>iVolunteer</div>
							<div className='prog'>iElect Volunteer Program</div>
						</div>
					</div>
					<div className='cert-ref'>
						<div className='ref-k'>Reference</div>
						<div className='ref-v'>{payload.reference}</div>
					</div>
				</div>

				<div className='cert-body'>
					<div className='cert-eyebrow'>Certificate of Application</div>
					<p className='cert-preline'>This certifies that</p>
					<h1 className='cert-name'>{name}</h1>
					<div className='cert-underline' />
					<div className='cert-role'>{roleLabel(payload.role)}</div>
					{payload.isTrustedAgent ? <div className='cert-trusted'>Trusted agent applicant</div> : null}
					<p className='cert-context'>
						has submitted an application to serve
						{payload.electionName ? (
							<>
								{' '}
								in the <strong>{payload.electionName}</strong>
							</>
						) : null}
						{payload.pollingUnitName ? (
							<>
								{' '}
								at <strong>{payload.pollingUnitName}</strong>
								{payload.pollingUnitCode ? ` (${payload.pollingUnitCode})` : ''}
							</>
						) : null}
						.
					</p>

					{party ? (
						<div className='cert-party'>
							<span
								className='party-seal'
								aria-hidden='true'
							>
								{partyInitials(payload.partyName || party)}
							</span>
							<span className='party-name'>{party}</span>
						</div>
					) : null}
				</div>

				<div className='cert-foot'>
					<div className='cert-meta'>
						<div className='k'>Polling Unit</div>
						<div className='v'>{payload.pollingUnitName || '—'}</div>
						<div
							className='k'
							style={{ marginTop: 6 }}
						>
							Unit Code
						</div>
						<div className='v'>{payload.pollingUnitCode || '—'}</div>
					</div>

					<div
						className='cert-seal'
						aria-hidden='true'
					>
						Received
					</div>

					<div className='cert-meta right'>
						<div className='k'>Submitted</div>
						<div className='v'>{formatSubmittedDate(payload.dateCreated) || '—'}</div>
						<div
							className='k'
							style={{ marginTop: 6 }}
						>
							Status
						</div>
						<div className='v'>Under review</div>
					</div>
				</div>

				<p className='cert-disclaimer'>
					This document certifies receipt of a volunteer application only. It is provisional and does not constitute proof of
					accreditation or deployment. Final selection is subject to review; successful applicants will be notified by email.
				</p>
			</div>
		</CertificateSheet>
	);
});

Certificate.displayName = 'Certificate';

export default Certificate;
